import type { StateCreator } from "zustand"
import { useMemo } from "react"
import { create } from "zustand"
import { persist } from "zustand/middleware"

import type {
	RoadmapFilterNegations,
	RoadmapSource,
} from "@productlane/zero/queries"
import { clientSafeRoadmapFilters } from "@productlane/zero/queries"

import type { RoadmapTeam } from "./roadmap-team-groups"
import { withoutNegation } from "@/components/filters"

export type RoadmapFilterNegationKey = keyof RoadmapFilterNegations

interface RoadmapFilterState {
	negations: RoadmapFilterNegations
	setNegation: (key: RoadmapFilterNegationKey, isNegated: boolean) => void

	statuses: string[]
	addStatus: (status: string) => void
	removeStatus: (status: string) => void
	clearStatuses: () => void

	teamIds: string[]
	toggleTeam: (team: RoadmapTeam) => void
	clearTeams: () => void

	projectIds: Array<string | null>
	addProject: (projectId: string | null) => void
	removeProject: (projectId: string | null) => void
	clearProjects: () => void

	priorities: string[]
	addPriority: (priority: string) => void
	removePriority: (priority: string) => void
	clearPriorities: () => void

	companyIds: Array<string | null>
	addCompany: (companyId: string | null) => void
	removeCompany: (companyId: string | null) => void
	clearCompanies: () => void

	sources: RoadmapSource[]
	toggleSource: (source: RoadmapSource) => void
	clearSources: () => void

	labelIds: string[]
	toggleLabels: (ids: readonly string[]) => void
	clearLabels: () => void

	reset: () => void
}

const filterDefinition: StateCreator<RoadmapFilterState> = (set) => ({
	negations: {},
	setNegation: (key, isNegated) => {
		set((prev) => ({
			negations: isNegated
				? { ...prev.negations, [key]: true }
				: withoutNegation(prev.negations, key),
		}))
	},

	statuses: [],
	addStatus: (status) => {
		set((prev) => ({ statuses: [...prev.statuses, status] }))
	},
	removeStatus: (status) => {
		set((prev) => ({ statuses: prev.statuses.filter((s) => s !== status) }))
	},
	clearStatuses: () => {
		set((prev) => ({
			statuses: [],
			negations: withoutNegation(prev.negations, "statuses"),
		}))
	},

	teamIds: [],
	toggleTeam: (team) => {
		set((prev) => {
			if (team.ids.some((id) => prev.teamIds.includes(id))) {
				return { teamIds: prev.teamIds.filter((id) => !team.ids.includes(id)) }
			}
			return { teamIds: [...prev.teamIds, team.id] }
		})
	},
	clearTeams: () => {
		set((prev) => ({
			teamIds: [],
			negations: withoutNegation(prev.negations, "teamIds"),
		}))
	},

	projectIds: [],
	addProject: (projectId) => {
		set((prev) => ({ projectIds: [...prev.projectIds, projectId] }))
	},
	removeProject: (projectId) => {
		set((prev) => ({
			projectIds: prev.projectIds.filter((id) => id !== projectId),
		}))
	},
	clearProjects: () => {
		set((prev) => ({
			projectIds: [],
			negations: withoutNegation(prev.negations, "projectIds"),
		}))
	},

	priorities: [],
	addPriority: (priority) => {
		set((prev) => ({ priorities: [...prev.priorities, priority] }))
	},
	removePriority: (priority) => {
		set((prev) => ({
			priorities: prev.priorities.filter((p) => p !== priority),
		}))
	},
	clearPriorities: () => {
		set((prev) => ({
			priorities: [],
			negations: withoutNegation(prev.negations, "priorities"),
		}))
	},

	companyIds: [],
	addCompany: (companyId) => {
		set((prev) => ({ companyIds: [...prev.companyIds, companyId] }))
	},
	removeCompany: (companyId) => {
		set((prev) => ({
			companyIds: prev.companyIds.filter((id) => id !== companyId),
		}))
	},
	clearCompanies: () => {
		set((prev) => ({
			companyIds: [],
			negations: withoutNegation(prev.negations, "companyIds"),
		}))
	},

	// One source at a time: Customers and Internal together match everything.
	sources: [],
	toggleSource: (source) => {
		set((prev) => ({
			sources: prev.sources.includes(source) ? [] : [source],
		}))
	},
	clearSources: () => {
		set({ sources: [] })
	},

	labelIds: [],
	toggleLabels: (ids) => {
		set((prev) => ({
			labelIds: ids.some((id) => prev.labelIds.includes(id))
				? prev.labelIds.filter((id) => !ids.includes(id))
				: [...prev.labelIds, ...ids],
		}))
	},
	clearLabels: () => {
		set((prev) => ({
			labelIds: [],
			negations: withoutNegation(prev.negations, "labelIds"),
		}))
	},

	reset: () => {
		set({
			negations: {},
			statuses: [],
			teamIds: [],
			projectIds: [],
			priorities: [],
			companyIds: [],
			sources: [],
			labelIds: [],
		})
	},
})

export const useRoadmapFilterState = create<RoadmapFilterState>()(
	persist(filterDefinition, {
		name: "roadmap-filter",
		version: 1,
		migrate: (persisted) =>
			clientSafeRoadmapFilters(persisted as RoadmapFilterState),
	}),
)

export function useIsRoadmapFilterActive() {
	const {
		statuses,
		teamIds,
		projectIds,
		priorities,
		companyIds,
		sources,
		labelIds,
	} = useRoadmapFilterState()
	return useMemo(
		() =>
			statuses.length > 0 ||
			teamIds.length > 0 ||
			projectIds.length > 0 ||
			priorities.length > 0 ||
			companyIds.length > 0 ||
			sources.length > 0 ||
			labelIds.length > 0,
		[
			statuses.length,
			teamIds.length,
			projectIds.length,
			priorities.length,
			companyIds.length,
			sources.length,
			labelIds.length,
		],
	)
}
